import { useState, useEffect } from "react";
import axios from "axios";
import {
  Coins, Settings2, ScrollText, RefreshCw, ChevronLeft, ChevronRight,
  Save, Plus, X, ShieldCheck, AlertCircle, CheckCircle2
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

const API = "https://api.komorotel.com/api/superadmin";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");
const fmtEUR = (n) => `${(n ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;
const fmtDateTime = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  completed: "Terminée",
  cancelled: "Annulée",
  pending_cancel: "Annulation demandée",
  no_show: "No-show",
};

const ACTION_LABELS = {
  SETTINGS_UPDATED: "Paramètres modifiés",
  SUPERADMIN_CREATED: "Super admin créé",
  ADMIN_CREATED: "Admin créé",
  ADMIN_ACTIVATED: "Admin activé",                          // 🆕 corrigé (était ADMIN_TOGGLED, ne matchait jamais)
  ADMIN_DEACTIVATED: "Admin désactivé",                       // 🆕 ajouté (manquait)
  ADMIN_PASSWORD_RESET_SENT: "Reset mot de passe envoyé",     // 🆕 corrigé (vrai nom backend)
  CLIENT_BLOCKED: "Client bloqué",                            // 🆕 corrigé (était CLIENT_BLOCK_TOGGLED, ne matchait jamais)
  CLIENT_UNBLOCKED: "Client débloqué",                        // 🆕 ajouté (manquait)
  REQUEST_VALIDATED: "Demande validée",
  REQUEST_REJECTED: "Demande rejetée",
  VISION_MODE: "Mode Vision",
  VISION_MODE_EDIT: "Modification en Mode Vision",
  HOTEL_ACTIVATED: "Hôtel activé",     // 🆕
  HOTEL_DEACTIVATED: "Hôtel désactivé", // 🆕
};

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all duration-150
        ${active
          ? "bg-white text-gray-900 font-medium shadow-sm border border-gray-200"
          : "text-gray-500 hover:text-gray-900"
        }`}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-sm text-sm
      ${ok ? "bg-white border-gray-200 text-gray-900" : "bg-red-50 border-red-200 text-red-700"}`}>
      {ok ? <CheckCircle2 size={16} className="text-[#3A9D23]" /> : <AlertCircle size={16} className="text-red-500" />}
      {toast.message}
    </div>
  );
}

// ─────────────────────────────────────────
// ONGLET FINANCES
// ─────────────────────────────────────────
function FinancesTab({ setToast }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFinances = (y) => {
    setLoading(true);
    axios.get(`${API}/finances?year=${y}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setToast({ type: "error", message: "Impossible de charger les finances." }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFinances(year); }, [year]);

  return (
    <div className="flex flex-col gap-5">

      {/* Sélecteur année + totaux */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-1 py-1">
          <button
            onClick={() => setYear(y => y - 1)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition"
          >
            <ChevronLeft size={15} />
          </button>
          <span className="text-sm font-medium text-gray-900 px-2 tabular-nums">{year}</span>
          <button
            onClick={() => setYear(y => Math.min(y + 1, currentYear))}
            disabled={year >= currentYear}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition disabled:opacity-30"
          >
            <ChevronRight size={15} />
          </button>
        </div>
        <button
          onClick={() => fetchFinances(year)}
          className="w-9 h-9 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
        >
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Totaux annuels */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "Volume brut " + year, value: fmtEUR(data?.totals?.gross), sub: data ? `${toKMF(data.totals.gross)} KMF` : "" },
          { label: "Commission Komorotel", value: fmtEUR(data?.totals?.commission), sub: data ? `${toKMF(data.totals.commission)} KMF` : "", accent: true },
          { label: "Réservations comptabilisées", value: data?.totals?.count ?? "…", sub: "Statuts confirmée + terminée" },
        ].map((kpi, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-400 mb-1">{kpi.label}</p>
            <p className={`text-2xl font-semibold tracking-tight tabular-nums ${kpi.accent ? "text-[#3A9D23]" : "text-gray-900"}`}>
              {loading ? "…" : kpi.value}
            </p>
            {kpi.sub && <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      {/* Graphique mensuel */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-medium text-gray-900 mb-4">Revenus mensuels {year}</p>
        <div className="h-64">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">Chargement…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.months || []} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false}
                  tickFormatter={(m) => m.slice(0, 3)} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => `${v} €`} />
                <Tooltip
                  cursor={{ fill: "#f9fafb" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                  formatter={(v, name) => [fmtEUR(v), name === "gross" ? "Volume brut" : "Commission"]}
                />
                <Bar dataKey="gross" fill="#d6ecc9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="commission" fill="#3A9D23" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex items-center gap-4 mt-3">
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#d6ecc9]" /> Volume brut
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#3A9D23]" /> Commission Komorotel
          </span>
        </div>
      </div>

      {/* Détail commissions par réservation */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">Commission par réservation</p>
          <p className="text-xs text-gray-400 mt-0.5">100 dernières réservations comptabilisées en {year}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">Réservation</th>
                <th className="px-4 py-2.5 font-medium">Hôtel</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Statut</th>
                <th className="px-4 py-2.5 font-medium text-right">Total</th>
                <th className="px-4 py-2.5 font-medium text-right">Commission</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Chargement…</td></tr>
              ) : !data?.recentCommissions?.length ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Aucune réservation sur cette période.</td></tr>
              ) : (
                data.recentCommissions.map(r => (
                  <tr key={r._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{r.reservationId}</td>
                    <td className="px-4 py-2.5 text-gray-900">{r.hotelId?.name || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{fmtDateTime(r.createdAt)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                        ${r.status === "completed" ? "bg-gray-100 text-gray-600" : "bg-[#eaf3de] text-[#3B6D11]"}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{fmtEUR(r.totalPrice)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[#3A9D23]">{fmtEUR(r.platformFee)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// ONGLET PARAMÈTRES
// ─────────────────────────────────────────
function SettingsTab({ setToast }) {
  const [settings, setSettings] = useState(null);
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true })
      .then(r => { setSettings(r.data); setRate(String(r.data.commissionRate)); })
      .catch(() => setToast({ type: "error", message: "Impossible de charger les paramètres." }));
    axios.get(`${API}/superadmins`, { withCredentials: true })
      .then(r => setSuperAdmins(r.data))
      .catch(() => {});
  }, []);

  const handleSaveRate = async () => {
    const val = parseFloat(rate);
    if (isNaN(val) || val < 0 || val > 100) {
      setToast({ type: "error", message: "Taux invalide (0 à 100)." });
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/settings`, { commissionRate: val }, { withCredentials: true });
      setSettings(r.data);
      setRate(String(r.data.commissionRate));
      setToast({ type: "success", message: "Taux de commission mis à jour." });
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.message || "Erreur lors de la sauvegarde." });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSuperAdmin = async () => {
    if (!form.name || !form.email || form.password.length < 8) {
      setToast({ type: "error", message: "Tous les champs sont requis (mot de passe ≥ 8 caractères)." });
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/superadmins`, form, { withCredentials: true });
      setSuperAdmins(list => [r.data, ...list]);
      setShowCreate(false);
      setForm({ name: "", email: "", password: "" });
      setToast({ type: "success", message: "Compte super admin créé." });
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.message || "Erreur lors de la création." });
    } finally {
      setCreating(false);
    }
  };

  const dirty = settings && parseFloat(rate) !== settings.commissionRate;

  return (
    <div className="flex flex-col gap-5 max-w-2xl">

      {/* Taux de commission */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <p className="text-sm font-medium text-gray-900">Taux de commission plateforme</p>
        <p className="text-xs text-gray-400 mt-0.5 mb-4">
          Appliqué sur le montant HT de chaque nouvelle réservation. Les réservations existantes ne sont pas recalculées.
        </p>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={rate}
              onChange={e => setRate(e.target.value)}
              className="w-32 pl-3 pr-9 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition tabular-nums"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
          </div>
          <button
            onClick={handleSaveRate}
            disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#3A9D23] text-white text-sm font-medium rounded-lg hover:bg-[#2d8019] transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={14} />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
        {settings && (
          <p className="text-xs text-gray-400 mt-3">
            Taux actuel : <span className="font-medium text-gray-600">{settings.commissionRate}%</span>
          </p>
        )}
      </div>

      {/* Comptes super admin */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Comptes super admin</p>
            <p className="text-xs text-gray-400 mt-0.5">{superAdmins.length} compte{superAdmins.length > 1 ? "s" : ""} avec accès complet</p>
          </div>
          <button
            onClick={() => setShowCreate(s => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            {showCreate ? <X size={14} /> : <Plus size={14} />}
            {showCreate ? "Annuler" : "Ajouter"}
          </button>
        </div>

        {showCreate && (
          <div className="flex flex-col gap-2.5 p-4 bg-gray-50 rounded-lg mb-4">
            <input
              type="text"
              placeholder="Nom complet"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
            />
            <input
              type="password"
              placeholder="Mot de passe (8 caractères min.)"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
            />
            <button
              onClick={handleCreateSuperAdmin}
              disabled={creating}
              className="self-start px-4 py-2 bg-[#3A9D23] text-white text-sm font-medium rounded-lg hover:bg-[#2d8019] transition disabled:opacity-40"
            >
              {creating ? "Création…" : "Créer le compte"}
            </button>
          </div>
        )}

        <div className="flex flex-col">
          {superAdmins.map(sa => (
            <div key={sa._id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
              <div className="w-8 h-8 rounded-full bg-[#eaf3de] text-[#3B6D11] flex items-center justify-center shrink-0">
                <ShieldCheck size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{sa.name}</p>
                <p className="text-xs text-gray-400 truncate">{sa.email}</p>
              </div>
              <p className="text-xs text-gray-400 shrink-0">
                Créé le {new Date(sa.createdAt).toLocaleDateString("fr-FR")}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// ONGLET AUDIT LOG
// ─────────────────────────────────────────
function AuditTab({ setToast }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/audit?page=${page}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setToast({ type: "error", message: "Impossible de charger le journal d'audit." }))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">Journal d'audit</p>
            <p className="text-xs text-gray-400 mt-0.5">{data?.total ?? "…"} action{(data?.total ?? 0) > 1 ? "s" : ""} enregistrée{(data?.total ?? 0) > 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Qui</th>
                <th className="px-4 py-2.5 font-medium">Action</th>
                <th className="px-4 py-2.5 font-medium">Cible</th>
                <th className="px-4 py-2.5 font-medium">Détails</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Chargement…</td></tr>
              ) : !data?.logs?.length ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Aucune action enregistrée pour le moment.</td></tr>
              ) : (
                data.logs.map(log => (
                  <tr key={log._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(log.createdAt)}</td>
                    <td className="px-4 py-2.5 text-gray-900">{log.superAdminName || log.superAdminId?.name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium whitespace-nowrap">
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                        {log.action === 'VISION_MODE_EDIT' && (
                          <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">
                            Mode Vision
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{log.target || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400 max-w-xs truncate">
                      {log.meta && Object.keys(log.meta).length ? JSON.stringify(log.meta) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data?.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-400 hover:bg-gray-50 transition disabled:opacity-30"
          >
            <ChevronLeft size={15} />
          </button>
          <span className="text-xs text-gray-500 tabular-nums">Page {page} / {data.pages}</span>
          <button
            onClick={() => setPage(p => Math.min(data.pages, p + 1))}
            disabled={page >= data.pages}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-gray-400 hover:bg-gray-50 transition disabled:opacity-30"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// PAGE PLATEFORME
// ─────────────────────────────────────────
export default function SuperAdminPlatform() {
  const [tab, setTab] = useState("finances");
  const [toast, setToast] = useState(null);

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Plateforme</h1>
        <p className="text-sm text-gray-400 mt-0.5">Finances Komorotel, paramètres globaux et journal d'audit</p>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 self-start">
        <TabButton active={tab === "finances"} onClick={() => setTab("finances")} icon={Coins} label="Finances" />
        <TabButton active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings2} label="Paramètres" />
        <TabButton active={tab === "audit"} onClick={() => setTab("audit")} icon={ScrollText} label="Audit log" />
      </div>

      {tab === "finances" && <FinancesTab setToast={setToast} />}
      {tab === "settings" && <SettingsTab setToast={setToast} />}
      {tab === "audit" && <AuditTab setToast={setToast} />}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
